<script setup lang="ts">
import { ref, onBeforeMount, reactive } from 'vue';
import i18n from '@/i18n';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { AIR_ONLY, CRUISE, HOTEL_ONLY, PACKAGE, CREATED, TOUR } from '@/constants';
import { formatDate } from '@/utils/dateUtils';
import { useIndexStore } from '@/store/IndexStore';
import { useGroupTypeStore } from '@/store/GroupTypeStore';
import { useAdminQuoteStore } from '@/store/AdminQuoteStore';
import { useUserAuthorizationsStore } from '@/store/UserAuthorizationsStore';
import { useViewComposable } from '@/views/composable';
import { FilterMatchMode, FilterOperator } from '@primevue/core/api';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import DatePicker from 'primevue/datepicker';
import InputText from 'primevue/inputtext';
import Select from 'primevue/select';
import TableColumn from 'primevue/column';
import LoadingSpinner from '@/components/LoadingSpinner.vue';
import ExportExcelFile from '@/components/modal/utils/ExportExcelFile.vue';
import FilterTable from '@/components/common/FilterTable.vue';
import ReportStats from '@/components/stats/ReportStats.vue';

const { t } = useI18n({ useScope: 'global' });
const { quoteSts, renderLocalTimeFromUtc } = useViewComposable();
const quotes = ref([]);
const startElement = ref(0);
const filters = ref();
const toast = useToast();
const loading1 = ref(false);
const sheetColumnStyle = [
  { wch: 10 },
  { wch: 20 },
  { wch: 20 },
  { wch: 12 },
  { wch: 12 },
  { wch: 10 },
  { wch: 10 },
  { wch: 15 },
  { wch: 15 },
  { wch: 20 },
  { wch: 20 },
  { wch: 15 },
  { wch: 10 },
  { wch: 10 },
  { wch: 10 },
  { wch: 10 },
  { wch: 10 },
  { wch: 10 },
  { wch: 40 },
  { wch: 10 },
  { wch: 40 },
  { wch: 10 },
  { wch: 40 },
  { wch: 40 },
  { wch: 16 },
  { wch: 10 },
  { wch: 16 },
  { wch: 10 }
];
const mapStatus = (status) => {
  const statusRs = quoteSts.value.find((item) => item.value === status);
  return statusRs ? statusRs.label : '';
};
const adminQuoteStore = useAdminQuoteStore();
const groupTypeManagement = useGroupTypeStore();
const userAuthorizationsStore = useUserAuthorizationsStore();
const authorizations: any = userAuthorizationsStore.permissionByRole;
const currentFieldFilter = ref('');
const filterGlobalField = ref('');
const sheetsData = ref([]);
const totalRecords = ref(0);
const groupTypeList = ref([]);
const isLoading = ref(false);
const pageSizes = ref([10, 25, 50, 100, 200, 400, 500]);

const minDateDefault = new Date(DateTime.utc().minus({ days: 30 }));
const maxDateDefault = new Date();

const initData = () => {
  return {
    typeOfSearch: CREATED,
    fromDate: minDateDefault,
    toDate: maxDateDefault,
    quoteType: [PACKAGE, HOTEL_ONLY, AIR_ONLY, CRUISE, TOUR],
    groupType: []
  };
};

const valueFilter = reactive(initData());

const initFilters = () => {
  filters.value = {
    global: { value: filterGlobalField.value, matchMode: FilterMatchMode.CONTAINS },
    quoteID: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    quoteSts: { operator: FilterOperator.OR, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    groupName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    agent: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    agency: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    asmName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    expires: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    departure: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    gateway: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    destination: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    hotelName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    hotelChain: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    audience: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    depositDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    finalPaymentDate: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.DATE_IS }] },
    netMargin: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    region: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cruiseShip: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    cruiseLineName: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    tourInclusionEn: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] },
    tourNameEn: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] }
  };
};
initFilters();

const getGroupTypes = async () => {
  await groupTypeManagement.getGroupTypeList().then((response) => {
    groupTypeList.value = response.data;
    valueFilter.groupType = getGroupTypeIDs(response.data);
  });
};

const getGroupTypeIDs = (groupTypes) => {
  return groupTypes.map((item) => item?.sk);
};

const handleFilter = (dataFilter) => {
  startElement.value = 0;
  if (dataFilter) {
    valueFilter.typeOfSearch = dataFilter.typeOfSearch;
    valueFilter.fromDate = dataFilter.fromDate;
    valueFilter.toDate = dataFilter.toDate;
    valueFilter.quoteType = dataFilter.quoteType;
    valueFilter.groupType = dataFilter.groupType;
  } else {
    valueFilter.typeOfSearch = initData().typeOfSearch;
    valueFilter.fromDate = initData().fromDate;
    valueFilter.toDate = initData().toDate;
    valueFilter.quoteType = initData().quoteType;
    valueFilter.groupType = getGroupTypeIDs(groupTypeList.value);
  }
  getListReportQuote();
};

async function fetchReport(payload: any) {
  await adminQuoteStore
    .getListReportQuotes(payload)
    .then((res) => {
      res.forEach((data) => {
        data.expires = new Date(data.expires);
        data.departure = new Date(data.departure);
        data.created = renderLocalTimeFromUtc(data.created);
        data.hotelName = renderValue([data.hotelName, data.cruiseShip, data.tourInclusionEn]);
        data.hotelChain = renderValue([data.hotelChain, data.cruiseLineName, data.tourNameEn]);
        const depositDate = new Date(data.depositDate);
        const finalPaymentDate = new Date(data.finalPaymentDate);
        data.depositDate = data.depositDate && new Date(depositDate.getTime() + depositDate.getTimezoneOffset() * 60000);
        data.finalPaymentDate = data.depositDate && new Date(finalPaymentDate.getTime() + finalPaymentDate.getTimezoneOffset() * 60000);
      });
      quotes.value = quotes.value.concat(res) || res;
    })
    .catch(() => {
      toast.add({
        severity: 'error',
        summary: '',
        detail: i18n.global.t('notification.unexpected_error'),
        life: 5000
      });
    });
}

const getListReportQuote = async () => {
  isLoading.value = true;
  quotes.value = [];
  totalRecords.value = 0;
  if (valueFilter.typeOfSearch === CREATED) {
    const fetchData = new Date(valueFilter.toDate);
    const intervalDay = 1;
    while (fetchData >= valueFilter.fromDate) {
      const fromDate = new Date(fetchData).setHours(0, 0, 0, 0);
      const toDate = new Date(fetchData).setHours(23, 59, 59, 0);
      const payload = {
        ...valueFilter,
        fromDate: formatDate(fromDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
        toDate: formatDate(toDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T')
      };
      await fetchReport(payload);

      fetchData.setDate(fetchData.getDate() - intervalDay);
    }
  } else {
    const fromDate = new Date(valueFilter.fromDate).setHours(0, 0, 0, 0);
    const toDate = new Date(valueFilter.toDate).setHours(23, 59, 59, 0);
    const payload = {
      ...valueFilter,
      fromDate: formatDate(fromDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T'),
      toDate: formatDate(toDate, 'yyyy-LL-dd HH:mm', true).replace(' ', 'T')
    };
    await fetchReport(payload);
  }
  sheetsData.value = [
    {
      name: 'Sheet 1',
      data: formatDataExport(quotes.value)
    }
  ];
  totalRecords.value = quotes.value.length;
  isLoading.value = false;
};

const renderValue = (data) => {
  return data.filter((e) => e !== null && e !== undefined).join(' / ');
};

const handleFilterApply = (filterCallback, filterField) => {
  currentFieldFilter.value = filterField;
  filterCallback();
};

const handleClearFilter = (filterCallback, filterField) => {
  currentFieldFilter.value = filterField;
  filterCallback();
};

const clearFilter = () => {
  initFilters();
  filterGlobalField.value = '';
  getListReportQuote().catch((e) => {
    const store = useIndexStore();
    store.updateNotification(e);
  });
};

const formatDataExport = (listData) => {
  return listData?.map((item) => {
    const data: any = {};
    data[t('my_quote.quote_id')] = item.quoteID;
    data[t('my_quote.create_date')] = new Date(item.created);
    data[t('my_quote.quote_status')] = item.quoteSts;
    data[t('report.assignee')] = item.assignedTo;
    data[t('my_quote.group_type')] = item.groupType;
    data[t('my_quote.group_name')] = item.groupName;
    data[t('my_quote.booking_number')] = item.bookingId || '';
    data[t('report.agent')] = item.agent || '';
    data[t('report.agent_email')] = item.email || '';
    data[t('report.agency')] = item.agency || '';
    data[t('report.agency_chain')] = item.agencyChain || '';
    data[t('report.agency_phone')] = item.agencyPhone || '';
    data[t('report.asm_region')] = item.region || '';
    data[t('report.asm_name')] = item.asmName || '';
    data[t('report.number_of_pax')] = item.numberOfPax;
    data[t('report.number_of_room')] = item.numberOfRoom;
    data[t('report.expiry')] = item.expires || '';
    data[t('report.departure')] = item.departure || '';
    data[t('common.gateway')] = item.gateway;
    data[t('report.gateway_airport_code')] = item.gatewayAirportCode;
    data[t('common.destination')] = item.destination;
    data[t('report.destination_airport_code')] = renderValue([item.destinationAirportCode, item.destId]);
    data[`${t('report.hotel_name')}/${t('report.cruise_ships')}/${t('report.tour_detail')}`] = item.hotelName;
    data[`${t('report.hotel_chain')}/${t('report.cruise_line_name')}/${t('report.tour_name')}`] = item.hotelChain;
    data[t('report.deposit_date')] = item.depositDate;
    data[t('report.deposit_amount')] = item.depositAmount;
    data[t('report.final_payment_date')] = item.finalPaymentDate;
    data[t('report.final_payment_amount')] = item.finalPayment;
    data[t('worksheet.average_net_margin')] = item.netMargin;
    if (!authorizations.report.net_margin_view) {
      delete data[t('worksheet.average_net_margin')];
    }
    return data;
  });
};

const statusEvent = (status: string) => {
  if (status === 'TOTAL') {
    clearFilter();
  } else {
    filters.value.quoteSts.constraints[0].value = status;
  }
  document.getElementById('dataTableRef')?.scrollIntoView({
    behavior: 'smooth',
    block: 'start',
    inline: 'nearest'
  });
};

onBeforeMount(async () => {
  await getGroupTypes();
  await getListReportQuote();
  loading1.value = false;
});
</script>

<template>
  <LoadingSpinner v-if="isLoading" />
  <ReportStats @status-event="statusEvent($event)" />
  <div class="filter-bar">
    <FilterTable :dataFilter="valueFilter" :groupTypeList="groupTypeList" @handleFilterData="handleFilter"></FilterTable>
  </div>
  <div class="grid quote-report" v-if="filters">
    <div class="col-12">
      <div class="card">
        <DataTable
          :scrollable="true"
          :value="quotes"
          :paginator="true"
          class="p-datatable-gridlines w-full text-sm"
          :rows="pageSizes[0]"
          :rowsPerPageOptions="pageSizes"
          dataKey="id"
          v-model:first="startElement"
          v-model:filters="filters"
          :totalRecords="totalRecords"
          filterDisplay="menu"
          :loading="loading1"
          responsiveLayout="stack"
          scrollHeight="900px"
          :globalFilterFields="[
            'agency',
            'agencyChain',
            'agencyPhone',
            'agent',
            'asmName',
            'assignedTo',
            'audience',
            'destId',
            'destination',
            'destinationAirportCode',
            'hotelName',
            'hotelChain',
            'language',
            'quoteID',
            'gateway',
            'gatewayAirportCode',
            'groupName',
            'groupType',
            'groupTypeFr',
            'region',
            'status'
          ]"
          rowGroupMode="rowspan"
          :groupRowsBy="['quoteID']"
        >
          <template #header>
            <div class="flex justify-content-between flex-col sm:flex-row">
              <div class="flex gap-2 align-items-center">
                <div class="flex justify-content-left flex-col sm:flex-row gap-3">
                  <Button type="button" id="clear-filter" icon="pi pi-filter-slash" :label="$t('common.buttons.clear')" class="p-button-secondary mb-2" @click="clearFilter()" />
                  <ExportExcelFile v-if="authorizations?.report?.report_export" :sheets="sheetsData" :columnsStyle="sheetColumnStyle" file-name="report.xlsx"></ExportExcelFile>
                </div>
                <div>({{ $t('quote_management.hint_30_message') }} )</div>
              </div>
              <div class="justify-content-right">
                <span class="p-input-icon-left mb-2">
                  <i class="pi pi-search" />
                  <InputText v-model="filters['global'].value" :placeholder="$t('my_quote.search_keyword')" class="w-full" />
                </span>
              </div>
            </div>
          </template>
          <template #empty> {{ $t('my_quote.no_quotes_found') }}</template>
          <template #loading> {{ $t('common.generic.loading') }}. {{ $t('my_quote.please_wait') }}. </template>
          <TableColumn field="quoteID" class="min-w-6rem" filter :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('my_quote.quote_id') }} /</div>
                <div>{{ $t('my_quote.create_date') }} /</div>
                <div class="white-space-nowrap">{{ $t('my_quote.booking_number') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <div class="flex w-full justify-content-between">
                <span class="quote-id-header hidden font-bold">
                  <div>
                    <div>{{ $t('my_quote.quote_id') }} /</div>
                    <div>{{ $t('my_quote.create_date') }} /</div>
                    <div class="white-space-nowrap">{{ $t('my_quote.booking_number') }}</div>
                  </div>
                </span>
                <div>
                  <p class="mb-2">{{ data.quoteID }}</p>
                  <div class="mb-2">
                    <strong>{{ $t('my_quote.created') }}</strong>
                    <p class="mb-0">
                      {{ formatDate(data.created, 'dd-LLL-yyyy', undefined, i18n.global.locale.value) }}
                    </p>
                    <p>{{ formatDate(data.created, 'HH:mm:ss') }}</p>
                  </div>
                  <div>
                    <strong class="text-sm">{{ $t('my_quote.booking_number') }}:</strong>
                    <p class="mb-0 text-primary">{{ data.bookingId || '-' }}</p>
                  </div>
                </div>
              </div>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('my_quote.quote_id')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'quoteID')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'quoteID')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn filter field="quoteSts" :header="$t('my_quote.quote_status')" class="min-w-8rem" :show-filter-operator="false" :show-add-button="false">
            <template #body="{ data }">
              <div>
                <span class="font-bold uppercase">{{ mapStatus(data.quoteSts) }}</span>
              </div>
            </template>
            <template #filter="{ filterModel }">
              <Select v-model="filterModel.value" :options="quoteSts" optionLabel="label" optionValue="value" :placeholder="$t('common.any')" class="p-column-filter w-14rem" :showClear="true">
                <template #option="slotProps">
                  <option>{{ slotProps.option.label }}</option>
                </template>
              </Select>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'status')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'status')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="groupName" class="break-word min-w-8rem" filter :show-filter-operator="false" :show-add-button="false">
            <template #header>{{ $t('report.group_name') }}</template>
            <template #body="{ data }">
              <span class="group-name-column" v-tooltip="data.groupName">{{ data.groupName }}</span>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('report.group_name')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'groupName')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'groupName')" class="p-button-success"></Button>
            </template>
          </TableColumn>

          <TableColumn field="agent" class="min-w-10rem" filter :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('report.agent') }} /</div>
                <div>{{ $t('report.agent_email') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <span>{{ data.agent }}</span>
              <br /><br />
              <span>{{ data.email }}</span>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" :placeholder="$t('report.agent')" class="p-column-filter" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'agent')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'agent')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn :show-filter-operator="false" :show-add-button="false" field="agency" class="min-w-11rem">
            <template #header>
              <div>
                <div>{{ $t('report.agency') }} /</div>
                <div>{{ $t('report.agency_phone') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <span>{{ data.agency }}</span>
              <br /><br />
              <span>{{ data.agencyPhone }}</span>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" class="p-column-filter" :placeholder="$t('report.agency')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'agency')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'agency')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="asmName" class="min-w-10rem" filter :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('report.asm_region') }} /</div>
                <div>{{ $t('report.asm_name') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <span>{{ data.region }}</span>
              <br /><br />
              <span>{{ data.asmName }}</span>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" :placeholder="$t('report.asm_name')" class="p-column-filter" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'asmName')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'asmName')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="numberOfPax" :header="$t('report.number_of_pax')" class="min-w-10rem" :show-filter-operator="false" :show-add-button="false">
            <template #body="{ data }">
              {{ data.numberOfPax }}
            </template>
          </TableColumn>
          <TableColumn field="numberOfRoom" :header="$t('report.number_of_room')" class="min-w-10rem" :show-filter-operator="false" :show-add-button="false">
            <template #body="{ data }">
              {{ data.numberOfRoom }}
            </template>
          </TableColumn>
          <TableColumn field="expires" :header="$t('report.expiry')" dataType="date" class="min-w-8rem" filter :show-filter-operator="false" :show-add-button="false">
            <template #body="{ data }">
              {{ formatDate(data.expires, 'dd-LLL-yyyy', undefined, i18n.global.locale.value) }}
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'expires')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'expires')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="departure" dataType="date" filter :header="$t('report.departure')" class="min-w-8rem" :show-filter-operator="false" :show-add-button="false">
            <template #body="{ data }">
              {{ formatDate(data.departure, 'dd-LLL-yyyy', undefined, i18n.global.locale.value) }}
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'expires')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'expires')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="gateway" filter :header="$t('common.gateway')" class="min-w-8rem" :show-filter-operator="false" :show-add-button="false">
            <template #body="{ data }">
              <div>
                <span>{{ data.gateway }}</span>
                <span>{{ data.gatewayAirportCode && `(${data.gatewayAirportCode})` }}</span>
              </div>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" v-model="filterModel.value" :placeholder="$t('common.gateway')" class="p-column-filter" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'gateway')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'gateway')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="destination" filter :header="$t('common.destination')" class="min-w-8rem" :show-filter-operator="false" :show-add-button="false">
            <template #body="{ data }">
              <div>
                <span>{{ data.destination }}</span>
                <span>{{ data.destId && `(${data.destId})` }}</span>
                <span>{{ data.destinationAirportCode && `(${data.destinationAirportCode})` }}</span>
              </div>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" :placeholder="$t('common.destination')" v-model="filterModel.value" class="p-column-filter" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'destination')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'destination')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="hotelName" class="min-w-12rem" filter :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('report.hotel_name') }} /</div>
                <div>{{ $t('report.cruise_ships') }} /</div>
                <div>{{ $t('report.tour_detail') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <span>{{ data.hotelName }}</span>
              <br /><br />
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" :placeholder="`${$t('report.hotel_name')}/ ${$t('report.cruise_ships')}`" v-model="filterModel.value" class="p-column-filter" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'hotelName')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'hotelName')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="hotelChain" class="min-w-12rem" filter :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('report.hotel_chain') }} /</div>
                <div>{{ $t('report.cruise_line_name') }}/</div>
                <div>{{ $t('report.tour_name') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <span>{{ data.hotelChain }}</span>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" :placeholder="`${$t('report.hotel_chain')}/ ${$t('report.cruise_line_name')}`" v-model="filterModel.value" class="p-column-filter" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'hotelChain')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'hotelChain')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="audience" class="min-w-8rem" filter :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('report.audience') }}</div>
                <div>{{ $t('voucher.language') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <p>{{ data.audience }}</p>
              <p class="font-bold uppercase">{{ data.language }}</p>
            </template>
            <template #filter="{ filterModel }">
              <InputText type="text" :placeholder="$t('report.audience')" v-model="filterModel.value" class="p-column-filter" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'audience')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'audience')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="depositDate" filter dataType="date" class="min-w-11rem" :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('report.deposit_date') }} /</div>
                <div>{{ $t('report.deposit_amount') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <span>{{ formatDate(data.depositDate, 'dd-LLL-yyyy hh:mm:ss', undefined, i18n.global.locale.value) }}</span>
              <br /><br />
              <span>{{ data.depositAmount.toFixed(2) }}</span>
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'depositDate')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'depositDate')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn field="finalPaymentDate" filter dataType="date" class="min-w-11rem" :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('report.final_payment_date') }} /</div>
                <div>{{ $t('report.final_payment_amount') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <span>{{ formatDate(data.finalPaymentDate, 'dd-LLL-yyyy hh:mm:ss', undefined, i18n.global.locale.value) }}</span>
              <br /><br />
              <span>{{ data.finalPayment.toFixed(2) }}</span>
            </template>
            <template #filter="{ filterModel }">
              <DatePicker v-model="filterModel.value" dateFormat="dd-M-yy" :placeholder="$t('common.date_format')">
                <template #footer>
                  <p class="text-center">
                    <small>{{ $t('common.press_space') }}</small>
                  </p>
                </template>
              </DatePicker>
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'finalPaymentDate')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'finalPaymentDate')" class="p-button-success"></Button>
            </template>
          </TableColumn>
          <TableColumn v-if="authorizations?.report?.net_margin_view" field="netMargin" filter class="min-w-11rem" :show-filter-operator="false" :show-add-button="false">
            <template #header>
              <div>
                <div>{{ $t('worksheet.average_net_margin') }}</div>
              </div>
            </template>
            <template #body="{ data }">
              <span>{{ data.netMargin ?? '0.00' }}</span>
            </template>
            <template #filter="{ filterModel }">
              <InputText v-model="filterModel.value" type="text" class="p-column-filter" :placeholder="$t('worksheet.average_net_margin')" />
            </template>
            <template #filterclear="{ filterCallback }">
              <Button type="button" icon="pi pi-times" @click="handleClearFilter(filterCallback, 'netMargin')" class="p-button-secondary"></Button>
            </template>
            <template #filterapply="{ filterCallback }">
              <Button type="button" icon="pi pi-check" @click="handleFilterApply(filterCallback, 'netMargin')" class="p-button-success"></Button>
            </template>
          </TableColumn>
        </DataTable>
      </div>
    </div>
  </div>
</template>
